import React, { Fragment, useMemo } from 'react';
import classNames from 'class-names';
import moment from 'moment';
import Container from "react-bootstrap/Container";
import Loader from "react-loader-spinner";
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from "react-redux";

import Icon from "../../components/Icon/Icon";
import Calendar from "./Calendar";
import { SubscriptionDetailsMissedUsage } from '../../components/subscriptions/SubscriptionDetailsMissedUsage';

import * as actions from '../../store/actions'

import { LOCALE_NAMESPACE } from '../../const/translations/LOCALE_NAMESPACE';

import Styles from './Details.module.scss';
import { formatPrice } from '../../helpers/function/format';

const SubscriptionDetail = ({ activeSubscription, t }) => {
    const { t: ts } = useTranslation(LOCALE_NAMESPACE.SUBSCRIPTIONS);

    const dispatch = useDispatch();
    const { style } = useSelector(({ widget }) => widget);
    const { tintColor } = useSelector(({ companies }) => companies);
    const {
        loading,
        subscriptionDetails,
        monthlyUsages,
        weeklyUsages
    } = useSelector(({ subscriptions }) => subscriptions);

    
    const { price, hide_price: isHidePrice } = activeSubscription ?? {};
    const isShowPrice = !(isHidePrice === true && Number(price) === 0);

    const [startDate, setStartDate] = React.useState(moment());

    const handleChange = (direction, periodicity) => {
        let nextStartDate;
        if (direction === 'left') {
            nextStartDate = periodicity === 'weekly' ? weeklyUsages.hasPrev : monthlyUsages.hasPrev;
        } else {
            nextStartDate = periodicity === 'weekly' ? weeklyUsages.hasNext : monthlyUsages.hasNext;
        }
        setStartDate(moment.unix(nextStartDate));
        dispatch(actions.getUsages(activeSubscription.client_subscription_id, nextStartDate));
    };

    const showDetails = (
        Boolean(activeSubscription.client_subscription_id)
        && String(activeSubscription.client_subscription_id) === String(subscriptionDetails.id)
    );

    const groupsWithActivatedMissedUsages = useMemo(() => {
        if (!showDetails || !subscriptionDetails.subscription?.groups) {
            return [];
        }

        return subscriptionDetails
            .subscription
            .groups
            .filter((g) => g.use_missed_usages_after > 0);
    }, [showDetails, subscriptionDetails.subscription?.groups]);

    const groupsWithActivatedMissedUsagesHash = useMemo(() => (
        groupsWithActivatedMissedUsages.reduce((res, g) => ({
            ...res,
            [g.id]: g,
        }), {})
    ), [groupsWithActivatedMissedUsages]);

    const areMissedUsagesActive = showDetails && Boolean(groupsWithActivatedMissedUsages.length);

    const missedUsages = subscriptionDetails.periods?.missed_usages || [];

    const showMissedUsages = showDetails && areMissedUsagesActive && Boolean(missedUsages.length);

    return (
        <>
            <Container
                className={[
                    Styles.container,
                    (style === 'dev' || style === 'website') ? Styles.website : ''
                ].join(' ')}
            >
                {loading ? (
                    <div><Loader type="TailSpin" color="#000" height={25} width={25}/></div>
                ) : (
                    <div className={'row'} style={{ width: '100%' }}>
                        <div className={'col-lg-6 col-12'}>
                            <div className={Styles.blockWrapper}>
                                {isShowPrice && (
                                    <div>
                                        <strong className={Styles.price}>{formatPrice(activeSubscription.price)}</strong>
                                        <span className={Styles.period}>{` ${t(`subscriptionsScreen.per.${activeSubscription.periodicity}`)}`}</span>
                                    </div>
                                )}
                                <table className={Styles.table}>
                                    <tbody>
                                        <tr>
                                            <td className={Styles.icon}><Icon name={'clock'} color={tintColor} size={18}/></td>
                                            <td className={Styles.tableText}>{`${t('subscriptionsScreen.contractLength')}:`}</td>
                                            <td><strong>{t(`subscriptionsScreen.duration.${activeSubscription.periodicity}`, {count: activeSubscription.duration})}</strong></td>
                                        </tr>
                                        {Boolean(activeSubscription.client_subscription_id && subscriptionDetails.started_at) && (
                                            <tr>
                                                <td className={Styles.icon}>
                                                    <Icon name={'calendar'} color={tintColor} size={18} />
                                                </td>
                                                <td className={Styles.tableText}>{`${t('subscriptionsScreen.startDate')}:`}</td>
                                                <td><strong>{moment.unix(subscriptionDetails.started_at).format('D MMMM YYYY')}</strong></td>
                                            </tr>
                                        )}
                                        {Boolean(activeSubscription.client_subscription_id && subscriptionDetails.ends_at) && (
                                            <tr>
                                                <td className={Styles.icon}>
                                                    <Icon name={'calendar'} color={tintColor} size={18} />
                                                </td>
                                                <td className={Styles.tableText}>{`${t('subscriptionsScreen.endDate')}:`}</td>
                                                <td><strong>{moment.unix(subscriptionDetails.ends_at).format('D MMMM YYYY')}</strong></td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <div className={Styles.blockWrapper}>
                                <h4 className={Styles.blockTitle}>{t('subscriptionsScreen.description')}</h4>
                                <div className={Styles.descriptionText}>
                                    {activeSubscription.description}
                                </div>
                            </div>
                        </div>
                        <div className={'col-lg-6 col-12'}>
                            <div className={Styles.blockWrapper}>
                                <h4 className={Styles.blockTitle}>{t('subscriptionsScreen.details')}</h4>
                                <ul className={Styles.servicesList}>
                                    {activeSubscription.groups.map((group) => {
                                        return (
                                            <li
                                                key={group.id}
                                                className={[Styles.text, Styles.servicesListItem].join(' ')}
                                            >
                                                {group?.services.map(({ name }, index) => {
                                                    return (
                                                        <strong key={index}>
                                                            {name}
                                                            {index !== group?.services.length - 1 && ', '}
                                                        </strong>
                                                    )
                                                })}
                                                <span>{` - ${group.max_usages === -1 ? '∞' : group.max_usages} ${t('subscriptionsScreen.usages')}/${t(`subscriptionsScreen.${group.periodicity}`)}`}</span>
                                            </li>
                                        )
                                    })}
                                </ul>
                            </div>
                            {showDetails && (
                                <>
                                    <div className={Styles.blockWrapper}>
                                        <h4 className={Styles.blockTitle}>{t('subscriptionsScreen.usagesLeft')}</h4>
                                        {monthlyUsages && monthlyUsages.usages && monthlyUsages.usages.length > 0 ? (
                                            <>
                                                <h6 className={Styles.periodTitle}>{t('subscriptionsScreen.monthly')}</h6>
                                                <Calendar
                                                    startDate={startDate}
                                                    hasNext={monthlyUsages && monthlyUsages.hasNext}
                                                    hasPrev={monthlyUsages && monthlyUsages.hasPrev}
                                                    handleChange={handleChange}
                                                    periodicity={'monthly'}
                                                />
                                                <ul className={Styles.servicesList}>
                                                    {monthlyUsages && monthlyUsages.usages.map((usage, index) => {
                                                        const [group] = activeSubscription.groups.filter((group) => group.id === usage.subscription_group_id);

                                                        return (
                                                            <li
                                                                key={index}
                                                                className={[Styles.text, Styles.servicesListItem].join(' ')}
                                                            >
                                                                {group?.services.map(({ name, id }) => {
                                                                    return (
                                                                        <Fragment key={id}>
                                                                            <strong>{name}</strong>
                                                                            <br />
                                                                        </Fragment>
                                                                    )
                                                                })}
                                                                <span>{` - ${usage.max_usages === -1 ? '∞' : usage.max_usages - usage.used} ${t('subscriptionsScreen.usagesLeft')}`}</span>
                                                            </li>
                                                        )
                                                    })}
                                                </ul>
                                            </>
                                        ) : null}
                                        {weeklyUsages && weeklyUsages.usages && weeklyUsages.usages.length > 0 ? (
                                            <>
                                                <h6 className={Styles.periodTitle}>{t('subscriptionsScreen.weekly')}</h6>
                                                <Calendar
                                                    startDate={startDate}
                                                    hasNext={weeklyUsages && weeklyUsages.hasNext}
                                                    hasPrev={weeklyUsages && weeklyUsages.hasPrev}
                                                    handleChange={handleChange}
                                                    periodicity={'weekly'}
                                                />
                                                <ul className={Styles.servicesList}>
                                                    {weeklyUsages && weeklyUsages.usages.map((usage, index) => {
                                                        const [group] = activeSubscription.groups.filter((group) => group.id === usage.subscription_group_id);

                                                        return (
                                                            <li
                                                                key={index}
                                                                className={[Styles.text, Styles.servicesListItem].join(' ')}
                                                            >
                                                                {group?.services.map((service, index) => {
                                                                    return (
                                                                        <strong key={index}>{service.name}</strong>
                                                                    )
                                                                })}
                                                                <span>{` - ${usage.max_usages === -1 ? '∞' : usage.max_usages - usage.used} ${t('subscriptionsScreen.usagesLeft')}`}</span>
                                                            </li>
                                                        )
                                                    })}
                                                </ul>
                                            </>
                                        ) : null}
                                    </div>
                                    {showMissedUsages && (
                                        <div className={Styles.blockWrapper}>
                                            <h4 className={Styles.blockTitle}>
                                                {ts('sold.details.missedUsages.label')}
                                            </h4>
                                            <ul className={Styles.servicesList}>
                                                {missedUsages.map((u) => (
                                                    <li
                                                        key={u.id}
                                                        className={classNames(Styles.text, Styles.servicesListItem)}
                                                    >
                                                        <SubscriptionDetailsMissedUsage
                                                            usage={u}
                                                            group={groupsWithActivatedMissedUsagesHash[u.subscription_group_id]}
                                                        />
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}
            </Container>
        </>
    )
}

export default SubscriptionDetail;
